import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import Logo from "./Logo";
import PioneerNavLink from "./PioneerNavLink";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import {
  BadgeCheck, ChevronDown, ClipboardCheck, FileSearch,
  Landmark, Menu, Network, ShieldCheck,
} from "lucide-react";

/**
 * PublicNav — THE navigation bar for every public (logged-out-reachable) page
 * (forge-20260609-003).
 *
 * Before this component, six pages hand-rolled four diverging navs (Contact's
 * brand name was slate-900-on-navy with a blue CTA, Disclaimer's bar was
 * white, Pioneers had no nav at all, and only Landing had a mobile menu).
 * Don't re-create a per-page nav — render `<PublicNav active="…" />` and add
 * any new link HERE so every page gets it at once.
 *
 * Auth state intentionally reads `!!user || !!token`: `token` comes from
 * localStorage synchronously, so the bar shows Dashboard on the very first
 * paint for a logged-in refresh — no Login-button flash while the async
 * `user` object loads (the #116 anti-flicker pattern, previously missing
 * from the landing page).
 */

const VERIFY_ITEMS = [
  { hash: "member-profile", icon: ShieldCheck, title: "Start Investor Qualification", sub: "Primary intake & profile", testid: "verify-start", bold: true },
  { hash: "identity", icon: FileSearch, title: "KYC / AML Verification", sub: "Identity & sanctions screening", testid: "verify-kyc" },
  { hash: "accreditation", icon: BadgeCheck, title: "Accredited / Qualified Investor Review", sub: "Reg D / Reg S eligibility", testid: "verify-accredited" },
  { hash: "data-transfer-consent", icon: ShieldCheck, title: "Data Transfer Consent", sub: "Authorize regulated partner transfer", testid: "verify-consent" },
  { hash: "partner-onboarding", icon: Landmark, title: "Regulated Partner Onboarding", sub: "Approved pathway transfer", testid: "verify-partner" },
  { hash: "introducer", icon: Network, title: "Introducer Application", sub: "Referral disclosure", testid: "verify-introducer" },
  { hash: "review-status", icon: ClipboardCheck, title: "Compliance Status", sub: "Track your application", testid: "verify-status" },
];

const PublicNav = ({ active }) => {
  const { user, token } = useAuth();
  const isAuthenticated = !!user || !!token;
  const { t } = useLanguage();
  const navigate = useNavigate();

  const linkCls = (key) =>
    active === key
      ? "text-gold font-semibold"
      : "text-platinum hover:text-gold transition-colors font-semibold";

  return (
    <nav
      className="sticky top-0 left-0 right-0 z-50 px-8 py-5 bg-obsidian/85 backdrop-blur-md border-b border-platinum/10 shadow-card"
      data-testid="public-nav"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Logo size="large" linkTo="/" />

        {/* Desktop nav — hidden on mobile/tablet, full nav on lg+.
            IA per forge-20260609-004: ONE solid-gold element (Get started);
            Login is a quiet text link; the verify funnel lives under a
            neutral dropdown; Disclaimer moved to the footers.

            STARSEED-CAPMARKETS-236 amends ONE word of that decision. The
            original wording put the verify funnel under a neutral *"Get
            Started"*, distinguishing it from the gold CTA by weight alone.
            Two controls then read "Get Started" in the same bar, and a
            visitor scanning left-to-right hit the dropdown first and was
            never registered — reported by the operator 2026-08-31 and
            reproduced logged-out at 1440px.
            The structure is unchanged (one gold CTA + one neutral dropdown);
            only the dropdown's LABEL moves off the collision. Do not rename
            it back to "Get Started". */}
        <div className="hidden lg:flex items-center gap-5">
          <Link to="/about" className={linkCls("about")} data-testid="nav-about">
            {t('nav.about')}
          </Link>
          <PioneerNavLink />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-platinum hover:text-gold transition-colors font-semibold"
                data-testid="nav-verify-portal"
              >
                {t('nav.verification')}
                <ChevronDown size={12} className="opacity-80" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-surface border-gold/20 text-platinum w-72 p-2 shadow-xl">
              <div className="px-3 py-2">
                <p className="text-[9px] uppercase tracking-[0.3em] text-gold/80 font-semibold">Compliance Review Portal</p>
                <p className="text-xs text-platinum/60 mt-1">Begin your investor verification.</p>
              </div>
              <DropdownMenuSeparator className="bg-gold/10" />
              {VERIFY_ITEMS.map(({ hash, icon: Icon, title, sub, testid, bold }) => (
                <DropdownMenuItem
                  key={hash}
                  onClick={() => navigate(`/verification#${hash}`)}
                  className="cursor-pointer focus:bg-gold/10 focus:text-gold hover:bg-gold/10 hover:text-gold py-2.5"
                  data-testid={`nav-${testid}`}
                >
                  <Icon size={14} className="mr-2 text-gold" />
                  <div>
                    <p className={bold ? "text-sm font-semibold" : "text-sm"}>{title}</p>
                    <p className="text-[11px] text-platinum/55">{sub}</p>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/contact" className={linkCls("contact")} data-testid="nav-contact">
            Contact
          </Link>

          <LanguageSelector showLabel={false} />

          {isAuthenticated ? (
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-gold hover:bg-gold-light text-navy-dark font-semibold shadow-md"
              data-testid="dashboard-button"
            >
              {t('nav.dashboard')}
            </Button>
          ) : (
            <>
              <Link
                to="/login"
                className="text-platinum/80 hover:text-gold transition-colors font-semibold"
                data-testid="login-button"
              >
                {t('nav.login')}
              </Link>
              <Link to="/register">
                <Button className="bg-gold hover:bg-gold-light text-navy-dark font-semibold shadow-md" data-testid="register-button">
                  {t('common.get_started')}
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile / Tablet nav — hamburger that opens a slide-in sheet
            with all the same links. Shown below lg breakpoint (≤1024px). */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageSelector showLabel={false} />
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
                className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-gold/40 bg-gold/10 text-gold hover:bg-gold/20 transition-colors"
              >
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-96 bg-surface border-l border-gold/20 text-platinum overflow-y-auto">
              <div className="flex flex-col gap-1 pt-8" data-testid="mobile-menu-content">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-2">Menu</p>

                <SheetClose asChild>
                  <Link to="/about" className="text-base font-semibold py-3 border-b border-white/5 hover:text-gold transition-colors" data-testid="m-nav-about">
                    {t('nav.about')}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/contact" className="text-base font-semibold py-3 border-b border-white/5 hover:text-gold transition-colors" data-testid="m-nav-contact">
                    Contact
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <PioneerNavLink variant="mobile" />
                </SheetClose>

                <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80 font-semibold mt-6 mb-2">Compliance Review Portal</p>
                {VERIFY_ITEMS.slice(0, 3).map(({ hash, icon: Icon, title, sub, testid }) => (
                  <SheetClose asChild key={hash}>
                    <button
                type="button"
                      onClick={() => navigate(`/verification#${hash}`)}
                      className="text-left flex items-start gap-2 py-2.5 border-b border-white/5 hover:text-gold transition-colors"
                      data-testid={`m-nav-${testid}`}
                    >
                      <Icon size={16} className="text-gold mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold">{title}</p>
                        <p className="text-[11px] text-platinum/55">{sub}</p>
                      </div>
                    </button>
                  </SheetClose>
                ))}

                {/* Primary CTAs */}
                <div className="mt-6 space-y-3">
                  {isAuthenticated ? (
                    <SheetClose asChild>
                      <Button
                        onClick={() => navigate("/dashboard")}
                        className="w-full bg-gold hover:bg-gold-light text-navy-dark font-semibold shadow-md"
                        data-testid="m-dashboard-button"
                      >
                        {t('nav.dashboard')}
                      </Button>
                    </SheetClose>
                  ) : (
                    <>
                      <SheetClose asChild>
                        <Link to="/register" className="block">
                          <Button className="w-full bg-gold hover:bg-gold-light text-navy-dark font-bold shadow-md h-12" data-testid="m-register-button">
                            {t('common.get_started')}
                          </Button>
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/login" className="block">
                          <Button variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold/10 font-semibold h-11" data-testid="m-login-button">
                            {t('nav.login')}
                          </Button>
                        </Link>
                      </SheetClose>
                    </>
                  )}
                </div>

                <p className="text-[11px] text-platinum/45 text-center mt-8 pb-4">
                  EchoSense Capital Club · By invitation only
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default PublicNav;
